import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Sparkles, Clock, Shield, FileText, Key, AlertCircle, 
  LogOut, Loader2, CheckCircle2, Home, Phone, History, 
  GitCompare, MessageSquareText, Upload, Crown, CreditCard, Settings,
  Download, Copy, AlertTriangle, FolderOpen, Layers
} from "lucide-react";
import BatchUpload from "@/components/kompakt/BatchUpload";
import { UpgradePrompt } from "@/components/kompakt/UpgradePrompt";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  exportAnalysisToPDF, 
  exportComparisonToPDF, 
  exportQAToPDF, 
  copyToClipboard, 
  formatAnalysisAsText, 
  formatComparisonAsText 
} from "@/lib/pdfExport";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useSEO, SEO_PRESETS } from "@/hooks/useSEO";

interface UsageData {
  email: string;
  plan: string;
  dailyLimit: number;
  usedToday: number;
  remaining: number;
}

interface ConfidentialityWarning {
  type: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
}

interface AnalysisResult {
  zusammenfassung: string;
  keywords: string[];
  sentiment: {
    overall: string;
    positive: number;
    neutral: number;
    negative: number;
  };
  themes: Array<{ theme: string; sentiment: string }>;
  language: string;
  category?: string;
  confidentialityWarnings?: ConfidentialityWarning[];
  summaryLength?: 'short' | 'medium' | 'detailed';
}

type SummaryLength = 'short' | 'medium' | 'detailed';

interface CompareResult {
  similarities: string[];
  differences: string[];
  uniqueDoc1: string[];
  uniqueDoc2: string[];
  summary: string;
  recommendation: string;
}

export default function KlartechKompakt() {
  // SEO für Kompakt-Tool
  useSEO(SEO_PRESETS.kompakt);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [apiKey, setApiKey] = useState<string | null>(localStorage.getItem('klartech_kompakt_api_key'));
  const [loginKey, setLoginKey] = useState("");
  const [showRegistration, setShowRegistration] = useState(false);
  const [showKeyWarning, setShowKeyWarning] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [isAsking, setIsAsking] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textInput, setTextInput] = useState("");
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  
  // Compare state
  const [compareText1, setCompareText1] = useState("");
  const [compareText2, setCompareText2] = useState("");
  const [compareFile1, setCompareFile1] = useState<File | null>(null);
  const [compareFile2, setCompareFile2] = useState<File | null>(null);
  const [compareResult, setCompareResult] = useState<CompareResult | null>(null);
  
  // Q&A state
  const [qaDocument, setQaDocument] = useState("");
  const [qaFile, setQaFile] = useState<File | null>(null);
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaAnswer, setQaAnswer] = useState("");
  
  // Summary length preference
  const [summaryLength, setSummaryLength] = useState<SummaryLength>('medium');

  // Handle checkout results from URL
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const plan = searchParams.get('plan');
    
    if (checkout === 'success') {
      toast.success(`Upgrade auf ${plan === 'pro' ? 'Pro' : 'Starter'} erfolgreich! Vielen Dank.`);
      // Clear params and refresh subscription
      setSearchParams({});
      if (apiKey) {
        checkSubscription();
      }
    } else if (checkout === 'cancelled') {
      toast.info("Checkout abgebrochen");
      setSearchParams({});
    }
  }, [searchParams]);

  useEffect(() => {
    if (apiKey) {
      fetchUsage();
      checkSubscription();
    }
  }, [apiKey]);

  const fetchUsage = async () => {
    if (!apiKey) return;

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-usage', {
        body: { apiKey }
      });

      if (error) throw error;
      setUsage(data);
    } catch (error) {
      console.error('Usage fetch error:', error);
    }
  };

  const checkSubscription = async () => {
    if (!apiKey) return;

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-check-subscription', {
        body: { apiKey }
      });

      if (error) throw error;
      if (data.subscription_end) {
        setSubscriptionEnd(data.subscription_end);
      }
      // Refresh usage to get updated plan
      fetchUsage();
    } catch (error) {
      console.error('Subscription check error:', error);
    }
  };

  const handleUpgrade = async (plan: 'starter' | 'pro') => {
    if (!apiKey) return;
    setIsUpgrading(true);

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-create-checkout', {
        body: { apiKey, plan }
      });

      if (error) throw error;
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error("Fehler beim Starten des Upgrades");
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!apiKey) return;

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-customer-portal', {
        body: { apiKey }
      });

      if (error) throw error;
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error("Fehler beim Öffnen der Abo-Verwaltung");
    }
  };

  const handleLogin = () => {
    if (!loginKey.trim()) {
      toast.error("Bitte geben Sie Ihren API-Key ein");
      return;
    }
    
    if (!loginKey.startsWith('kk_')) {
      toast.error("Ungültiges API-Key Format");
      return;
    }
    
    setApiKey(loginKey);
    localStorage.setItem('klartech_kompakt_api_key', loginKey);
    toast.success("Erfolgreich angemeldet!");
  };

  const handleLogout = () => {
    setApiKey(null);
    localStorage.removeItem('klartech_kompakt_api_key');
    setLoginKey("");
    setEmail("");
    setShowRegistration(false);
    setShowKeyWarning(false);
    setUsage(null);
    setResult(null);
    setCompareResult(null);
    setQaAnswer("");
    toast.info("Abgemeldet");
  };

  const handleRegister = async () => {
    if (!email || !email.includes('@')) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }

    setIsRegistering(true);

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-register', {
        body: { email }
      });

      if (error) throw error;

      if (data.apiKey) {
        setApiKey(data.apiKey);
        localStorage.setItem('klartech_kompakt_api_key', data.apiKey);
        setShowKeyWarning(true);
        toast.success("Erfolgreich registriert! Bitte sichern Sie Ihren API-Key!");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Registrierung fehlgeschlagen";
      toast.error(errorMessage);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setResult(null);

      // If PDF, extract text
      if (file.type === 'application/pdf') {
        toast.info("PDF wird verarbeitet...");
        try {
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onload = () => {
              const base64 = reader.result as string;
              resolve(base64.split(',')[1]);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          
          const base64Content = await base64Promise;
          const { data, error } = await supabase.functions.invoke('kompakt-extract-pdf', {
            body: { apiKey, base64Content, fileName: file.name }
          });

          if (error) throw error;
          
          setTextInput(data.text);
          toast.success(`PDF extrahiert: ${data.pages} Seiten, ${data.characters} Zeichen`);
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : "PDF-Extraktion fehlgeschlagen";
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleAnalyze = async () => {
    if (!apiKey) {
      toast.error("Bitte melden Sie sich zuerst an");
      return;
    }

    let text = textInput;
    let fileName = "Texteingabe";

    if (selectedFile && selectedFile.type !== 'application/pdf') {
      text = await selectedFile.text();
      fileName = selectedFile.name;
    } else if (selectedFile) {
      fileName = selectedFile.name;
    }

    if (!text || text.length < 50) {
      toast.error("Der Text muss mindestens 50 Zeichen lang sein");
      return;
    }

    if (usage && usage.usedToday >= usage.dailyLimit) {
      toast.error("Tageslimit erreicht!");
      return;
    }

    setIsAnalyzing(true);

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-analyze', {
        body: { apiKey, text, fileName, summaryLength }
      });

      if (error) throw error;

      setResult(data);
      toast.success("Analyse erfolgreich!");
      fetchUsage();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Analyse fehlgeschlagen";
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Helper function to extract text from file
  const extractTextFromFile = async (file: File): Promise<string> => {
    if (file.type === 'application/pdf') {
      // Extract PDF text
      const base64Promise = new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const base64Content = await base64Promise;
      const { data, error } = await supabase.functions.invoke('kompakt-extract-pdf', {
        body: { apiKey, base64Content, fileName: file.name }
      });

      if (error) throw error;
      return data.text;
    } else {
      // Read as text
      return await file.text();
    }
  };

  const handleCompareFileChange = async (fileNum: 1 | 2, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (fileNum === 1) {
      setCompareFile1(file);
    } else {
      setCompareFile2(file);
    }

    try {
      const text = await extractTextFromFile(file);
      if (fileNum === 1) {
        setCompareText1(text);
      } else {
        setCompareText2(text);
      }
      toast.success(`${file.name} geladen`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Datei konnte nicht gelesen werden";
      toast.error(errorMessage);
    }
  };

  const handleQaFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setQaFile(file);

    try {
      const text = await extractTextFromFile(file);
      setQaDocument(text);
      toast.success(`${file.name} geladen`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Datei konnte nicht gelesen werden";
      toast.error(errorMessage);
    }
  };

  const handleCompare = async () => {
    if (!apiKey) {
      toast.error("Bitte melden Sie sich zuerst an");
      return;
    }

    if (!compareText1 || !compareText2) {
      toast.error("Bitte geben Sie beide Texte ein oder laden Sie Dateien hoch");
      return;
    }

    setIsComparing(true);

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-compare', {
        body: { 
          apiKey, 
          text1: compareText1, 
          text2: compareText2, 
          title1: compareFile1?.name || 'Dokument 1', 
          title2: compareFile2?.name || 'Dokument 2' 
        }
      });

      if (error) throw error;

      setCompareResult(data);
      toast.success("Vergleich abgeschlossen!");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Vergleich fehlgeschlagen";
      toast.error(errorMessage);
    } finally {
      setIsComparing(false);
    }
  };

  const handleAsk = async () => {
    if (!apiKey) {
      toast.error("Bitte melden Sie sich zuerst an");
      return;
    }

    if (!qaDocument || !qaQuestion) {
      toast.error("Bitte geben Sie Dokument und Frage ein");
      return;
    }

    setIsAsking(true);

    try {
      const { data, error } = await supabase.functions.invoke('kompakt-ask', {
        body: { apiKey, documentText: qaDocument, question: qaQuestion }
      });

      if (error) throw error;

      setQaAnswer(data.answer);
      toast.success("Antwort erhalten!");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Q&A fehlgeschlagen";
      toast.error(errorMessage);
    } finally {
      setIsAsking(false);
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positiv': return 'bg-green-500';
      case 'negativ': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const isPro = usage?.plan === 'pro' || usage?.plan === 'admin';
  const isStarter = usage?.plan === 'starter' || isPro;

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      {/* Header - matching main site */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="relative">
              <img 
                src="/images/logo-klartech.png" 
                alt="Klartech Müritz Logo" 
                className="h-12 w-auto object-contain transition-transform hover:scale-105"
              />
            </div>
          </Link>
          <nav className="flex items-center gap-4 md:gap-6">
            {apiKey && isStarter && (
              <Link to="/kompakt/history">
                <Button variant="ghost" size="sm" className="text-slate-700 hover:text-primary">
                  <History className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Historie</span>
                </Button>
              </Link>
            )}
            <a 
              href="tel:+4915128958006" 
              className="hidden md:flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-primary transition-colors"
            >
              <Phone className="h-4 w-4" />
              01512 895 800 6
            </a>
            <Link to="/">
              <Button 
                variant="outline" 
                size="sm"
                className="rounded-xl border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all"
              >
                <Home className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Startseite</span>
              </Button>
            </Link>
          </nav>
        </div>
      </header>
      
      <main className="flex-1">
        {/* Hero Section - matching main site */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white relative overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-cyan-200/20 to-blue-200/30 rounded-full blur-3xl"></div>
          
          <div className="mx-auto max-w-7xl px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 px-4 py-2 text-sm font-medium text-slate-700 mb-6 shadow-sm">
                <Sparkles className="h-4 w-4 text-primary" />
                Schnell • Klar • Nutzbar
              </div>
              
              <h1 className="text-balance text-4xl font-black tracking-tight text-slate-900 sm:text-5xl lg:text-6xl leading-tight mb-6">
                Klartech{" "}
                <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                  Kompakt
                </span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-4">
                KI-gestützte Dokumentenanalyse: <strong className="text-slate-700">Zusammenfassung, Keywords, Sentiment</strong> und mehr.
              </p>
              <p className="text-slate-500">
                Kostenlos starten – 3 Analysen pro Tag inklusive.
              </p>
            </div>
          </div>
        </section>

        {/* Features - matching main site style */}
        <section className="py-12 bg-gradient-to-b from-white to-slate-50">
          <div className="mx-auto max-w-7xl px-6">
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="group relative rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:scale-105">
                <div className="absolute -top-6 left-8">
                  <div className="inline-flex rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-4 shadow-lg">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">KI-gestützt</h3>
                  <p className="text-slate-600 leading-relaxed">Präzise Zusammenfassungen und relevante Keywords durch modernste KI-Technologie.</p>
                </div>
              </div>

              <div className="group relative rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:scale-105">
                <div className="absolute -top-6 left-8">
                  <div className="inline-flex rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-4 shadow-lg">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Blitzschnell</h3>
                  <p className="text-slate-600 leading-relaxed">Ergebnisse in Sekunden – sparen Sie wertvolle Zeit bei der Dokumentenanalyse.</p>
                </div>
              </div>

              <div className="group relative rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:scale-105">
                <div className="absolute -top-6 left-8">
                  <div className="inline-flex rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-4 shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Sicher & Privat</h3>
                  <p className="text-slate-600 leading-relaxed">Sichere Datenverarbeitung – Ihre Dokumente sind bei uns geschützt.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 pb-16 bg-white">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-6xl mx-auto">
              
              {/* Auth Section */}
              {!apiKey ? (
                <Card className="max-w-md mx-auto rounded-3xl border border-slate-200 shadow-xl">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-5 h-5 text-primary" />
                      <CardTitle className="text-slate-900">Anmelden / Registrieren</CardTitle>
                    </div>
                    <CardDescription className="text-slate-600">
                      {showRegistration 
                        ? "Erstmalige Registrierung mit E-Mail" 
                        : "Melden Sie sich mit Ihrem API-Key an"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!showRegistration ? (
                      <>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">API-Key</label>
                          <Input
                            type="text"
                            placeholder="kk_..."
                            value={loginKey}
                            onChange={(e) => setLoginKey(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                            className="border-slate-200 focus:border-primary"
                          />
                        </div>
                        <Button onClick={handleLogin} className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all">
                          Anmelden
                        </Button>
                        <div className="text-center">
                          <button
                            onClick={() => setShowRegistration(true)}
                            className="text-sm text-primary hover:underline font-medium"
                          >
                            Noch kein Account? Jetzt registrieren →
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Alert className="border-orange-200 bg-orange-50 rounded-xl">
                          <AlertCircle className="h-4 w-4 text-orange-600" />
                          <AlertDescription className="text-xs text-orange-900">
                            <strong>Wichtig!</strong> Nach der Registrierung erhalten Sie einen API-Key. 
                            <strong> Speichern Sie diesen sicher ab!</strong>
                          </AlertDescription>
                        </Alert>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">E-Mail</label>
                          <Input
                            type="email"
                            placeholder="name@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleRegister()}
                            className="border-slate-200 focus:border-primary"
                          />
                        </div>
                        <Button onClick={handleRegister} disabled={isRegistering} className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all">
                          {isRegistering ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Registriere...
                            </>
                          ) : "Registrieren"}
                        </Button>
                        <div className="text-center">
                          <button
                            onClick={() => setShowRegistration(false)}
                            className="text-sm text-primary hover:underline font-medium"
                          >
                            ← Zurück zur Anmeldung
                          </button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Logged in - Show API Key Warning and Usage */}
                  {showKeyWarning && (
                    <Alert className="max-w-2xl mx-auto mb-6 border-red-200 bg-red-50 dark:bg-red-950/20">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-sm text-red-900 dark:text-red-200">
                        <strong>WICHTIG!</strong> Ihr API-Key: <code className="bg-red-100 dark:bg-red-900/30 px-1 rounded text-xs break-all">{apiKey}</code>
                        <br />Speichern Sie diesen Key! Er wird nur einmal angezeigt.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Usage & Plan */}
                  <Card className="max-w-2xl mx-auto mb-8 rounded-2xl border border-slate-200 shadow-lg bg-white">
                    <CardContent className="py-5">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className="text-sm text-slate-500">{usage?.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={usage?.plan === 'pro' ? 'default' : usage?.plan === 'starter' ? 'secondary' : 'outline'} className="rounded-full">
                                {usage?.plan === 'pro' ? 'Pro' : usage?.plan === 'starter' ? 'Starter' : 'Free'}
                              </Badge>
                              {isPro && <Crown className="h-4 w-4 text-yellow-500" />}
                              {subscriptionEnd && (
                                <span className="text-xs text-slate-500">
                                  bis {new Date(subscriptionEnd).toLocaleDateString('de-DE')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                          {usage && (
                            <div className="text-right">
                              <p className="text-sm font-semibold text-slate-800">{usage.usedToday} / {usage.dailyLimit === 9999 ? '∞' : usage.dailyLimit}</p>
                              <p className="text-xs text-slate-500">Heute genutzt</p>
                            </div>
                          )}
                          {/* Upgrade/Manage Buttons */}
                          {usage?.plan === 'free' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleUpgrade('starter')}
                              disabled={isUpgrading}
                              className="rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all"
                            >
                              {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1" />}
                              Upgrade
                            </Button>
                          )}
                          {usage?.plan === 'starter' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleUpgrade('pro')}
                              disabled={isUpgrading}
                              className="rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 hover:shadow-lg transition-all text-yellow-950"
                            >
                              {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4 mr-1" />}
                              Pro
                            </Button>
                          )}
                          {(usage?.plan === 'starter' || usage?.plan === 'pro') && (
                            <Button variant="ghost" size="sm" onClick={handleManageSubscription} className="text-slate-600 hover:text-slate-900">
                              <Settings className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-xl border-slate-300 text-slate-700 hover:bg-slate-100">
                            <LogOut className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Abmelden</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pricing Cards for Free Users */}
                  {usage?.plan === 'free' && (
                    <div className="max-w-4xl mx-auto mb-8 grid md:grid-cols-2 gap-6">
                      <Card className="rounded-3xl border-2 border-primary/30 hover:border-primary/50 transition-all hover:shadow-xl bg-white">
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl text-slate-900">Starter</CardTitle>
                              <CardDescription className="text-slate-600">Für regelmäßige Nutzer</CardDescription>
                            </div>
                            <Badge variant="secondary" className="rounded-full bg-blue-100 text-primary">9,90€/Monat</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <ul className="text-sm space-y-2">
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              30 Dokumente pro Monat
                            </li>
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Analyseverlauf
                            </li>
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Alle Basisfeatures
                            </li>
                          </ul>
                          <Button 
                            className="w-full mt-4 rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all" 
                            onClick={() => handleUpgrade('starter')}
                            disabled={isUpgrading}
                          >
                            {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Starter wählen
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="rounded-3xl border-2 border-yellow-500/50 hover:border-yellow-500 transition-all hover:shadow-xl bg-gradient-to-br from-yellow-50 to-amber-50/30">
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
                                Pro <Crown className="h-5 w-5 text-yellow-500" />
                              </CardTitle>
                              <CardDescription className="text-slate-600">Voller Funktionsumfang</CardDescription>
                            </div>
                            <Badge className="rounded-full bg-yellow-500 text-yellow-950">19,90€/Monat</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <ul className="text-sm space-y-2">
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Unbegrenzte Dokumente
                            </li>
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Dokumentenvergleich
                            </li>
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Q&A-Funktion
                            </li>
                            <li className="flex items-center gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Alles aus Starter
                            </li>
                          </ul>
                          <Button 
                            className="w-full mt-4 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 hover:shadow-lg transition-all text-yellow-950" 
                            onClick={() => handleUpgrade('pro')}
                            disabled={isUpgrading}
                          >
                            {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
                            Pro wählen
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Tabs for Features */}
                  <Tabs defaultValue="analyze" className="max-w-6xl mx-auto">
                    <TabsList className="grid w-full grid-cols-4 mb-8 p-1 bg-slate-100 rounded-2xl">
                      <TabsTrigger value="analyze" className="flex items-center gap-1 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
                        <FileText className="h-4 w-4" />
                        <span className="hidden sm:inline">Analysieren</span>
                      </TabsTrigger>
                      <TabsTrigger value="batch" className="flex items-center gap-1 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm" disabled={!isPro}>
                        <Layers className="h-4 w-4" />
                        <span className="hidden sm:inline">Batch</span>
                        {!isPro && <Crown className="h-3 w-3 text-yellow-500 ml-1" />}
                      </TabsTrigger>
                      <TabsTrigger value="compare" className="flex items-center gap-1 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm" disabled={!isPro}>
                        <GitCompare className="h-4 w-4" />
                        <span className="hidden sm:inline">Vergleichen</span>
                        {!isPro && <Crown className="h-3 w-3 text-yellow-500 ml-1" />}
                      </TabsTrigger>
                      <TabsTrigger value="ask" className="flex items-center gap-1 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm" disabled={!isPro}>
                        <MessageSquareText className="h-4 w-4" />
                        <span className="hidden sm:inline">Fragen</span>
                        {!isPro && <Crown className="h-3 w-3 text-yellow-500 ml-1" />}
                      </TabsTrigger>
                    </TabsList>

                    {/* Analyze Tab */}
                    <TabsContent value="analyze">
                      {/* Show Upgrade Prompt when Free limit is reached */}
                      {usage?.plan === 'free' && usage.usedToday >= usage.dailyLimit ? (
                        <UpgradePrompt onUpgrade={handleUpgrade} isUpgrading={isUpgrading} />
                      ) : (
                      <div className="grid lg:grid-cols-2 gap-6">
                        {/* Input Card */}
                        <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-slate-900">
                              <Upload className="w-5 h-5 text-primary" />
                              Text oder Datei
                            </CardTitle>
                            <CardDescription className="text-slate-600">
                              PDF, TXT oder direkte Texteingabe
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <Textarea
                              placeholder="Fügen Sie hier Ihren Text ein (mind. 50 Zeichen)..."
                              value={textInput}
                              onChange={(e) => setTextInput(e.target.value)}
                              rows={8}
                              className="resize-none border-slate-200 focus:border-primary"
                            />
                            
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <span>oder</span>
                              <div className="flex-1 border-t border-slate-200" />
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <Input
                                type="file"
                                accept=".txt,.md,.csv,.json,.pdf"
                                onChange={handleFileChange}
                                className="flex-1 border-slate-200"
                              />
                              {selectedFile && (
                                <Badge variant="secondary" className="whitespace-nowrap text-xs rounded-full bg-blue-100 text-primary">
                                  {selectedFile.name.substring(0, 20)}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Summary Length Selector */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-slate-700">Zusammenfassungslänge</label>
                              <Select value={summaryLength} onValueChange={(value: SummaryLength) => setSummaryLength(value)}>
                                <SelectTrigger className="border-slate-200">
                                  <SelectValue placeholder="Länge wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="short">Kurz (50-100 Wörter)</SelectItem>
                                  <SelectItem value="medium">Mittel (150-200 Wörter)</SelectItem>
                                  <SelectItem value="detailed">Ausführlich (300-500 Wörter)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <Button
                              onClick={handleAnalyze}
                              disabled={isAnalyzing || (!textInput && !selectedFile)}
                              className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all"
                              size="lg"
                            >
                              {isAnalyzing ? (
                                <>
                                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                  Analysiere...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="mr-2 h-5 w-5" />
                                  Analysieren
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>

                        {/* Results Card */}
                        <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-slate-900">
                              <CheckCircle2 className="w-5 h-5 text-primary" />
                              Ergebnis
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {result ? (
                              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                                <div>
                                  <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1">Zusammenfassung</h4>
                                  <p className="text-sm leading-relaxed text-slate-700">{result.zusammenfassung}</p>
                                </div>
                                
                                {result.keywords?.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1">Keywords</h4>
                                    <div className="flex flex-wrap gap-1">
                                      {result.keywords.map((kw, idx) => (
                                        <Badge key={idx} variant="secondary" className="text-xs rounded-full bg-blue-100 text-primary">{kw}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {result.sentiment && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1">Sentiment</h4>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2 text-xs text-slate-700">
                                        <span className="w-14">Positiv:</span>
                                        <Progress value={result.sentiment.positive} className="flex-1 h-1.5" />
                                        <span className="w-8 text-right">{result.sentiment.positive}%</span>
                                      </div>
                                      <div className="flex items-center gap-2 text-xs text-slate-700">
                                        <span className="w-14">Neutral:</span>
                                        <Progress value={result.sentiment.neutral} className="flex-1 h-1.5" />
                                        <span className="w-8 text-right">{result.sentiment.neutral}%</span>
                                      </div>
                                      <div className="flex items-center gap-2 text-xs text-slate-700">
                                        <span className="w-14">Negativ:</span>
                                        <Progress value={result.sentiment.negative} className="flex-1 h-1.5" />
                                        <span className="w-8 text-right">{result.sentiment.negative}%</span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {result.themes?.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1">Themen</h4>
                                    <div className="space-y-0.5">
                                      {result.themes.map((theme, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-xs">
                                          <span className="text-slate-700">{theme.theme}</span>
                                          <Badge variant="outline" className="text-xs rounded-full">{theme.sentiment}</Badge>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Document Category */}
                                {result.category && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1">Dokumentenkategorie</h4>
                                    <Badge className="rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
                                      <FolderOpen className="h-3 w-3 mr-1" />
                                      {result.category}
                                    </Badge>
                                  </div>
                                )}
                                
                                {/* Confidentiality Warnings */}
                                {result.confidentialityWarnings && result.confidentialityWarnings.length > 0 && (
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                                      <Shield className="h-3 w-3" />
                                      Vertraulichkeitshinweise
                                    </h4>
                                    <Alert className="border-amber-200 bg-amber-50">
                                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                                      <AlertDescription className="text-xs">
                                        <p className="font-medium text-amber-800 mb-2">
                                          Sensible Daten erkannt ({result.confidentialityWarnings.length} Typen):
                                        </p>
                                        <ul className="space-y-1">
                                          {result.confidentialityWarnings.map((warning, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                              <Badge 
                                                variant="outline" 
                                                className={`text-[10px] rounded-full ${
                                                  warning.severity === 'high' ? 'border-red-300 bg-red-50 text-red-700' :
                                                  warning.severity === 'medium' ? 'border-amber-300 bg-amber-50 text-amber-700' :
                                                  'border-slate-300 bg-slate-50 text-slate-600'
                                                }`}
                                              >
                                                {warning.severity === 'high' ? 'Hoch' : warning.severity === 'medium' ? 'Mittel' : 'Niedrig'}
                                              </Badge>
                                              <span className="text-amber-800">
                                                <strong>{warning.type}:</strong> {warning.description}
                                              </span>
                                            </li>
                                          ))}
                                        </ul>
                                      </AlertDescription>
                                    </Alert>
                                  </div>
                                )}
                                
                                {/* Export Buttons */}
                                <div className="flex gap-2 pt-4 border-t border-slate-200">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 rounded-xl"
                                    onClick={() => {
                                      exportAnalysisToPDF({
                                        fileName: selectedFile?.name || 'Texteingabe',
                                        summary: result.zusammenfassung,
                                        keywords: result.keywords,
                                        sentiment: result.sentiment,
                                        themes: result.themes,
                                        language: result.language
                                      });
                                      toast.success("PDF heruntergeladen");
                                    }}
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    PDF
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 rounded-xl"
                                    onClick={() => {
                                      copyToClipboard(formatAnalysisAsText({
                                        fileName: selectedFile?.name || 'Texteingabe',
                                        summary: result.zusammenfassung,
                                        keywords: result.keywords,
                                        sentiment: result.sentiment,
                                        themes: result.themes,
                                        language: result.language
                                      }));
                                      toast.success("In Zwischenablage kopiert");
                                    }}
                                  >
                                    <Copy className="h-4 w-4 mr-1" />
                                    Kopieren
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                                <FileText className="w-10 h-10 mb-3 opacity-30" />
                                <p className="text-sm">Hier erscheint Ihre Analyse</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                      )}
                    </TabsContent>

                    {/* Batch Upload Tab (Pro only) */}
                    <TabsContent value="batch">
                      <BatchUpload apiKey={apiKey!} onUsageUpdate={fetchUsage} />
                    </TabsContent>

                    {/* Compare Tab */}
                    <TabsContent value="compare">
                      <div className="grid lg:grid-cols-2 gap-6">
                        <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
                          <CardHeader>
                            <CardTitle className="text-slate-900">Dokumente eingeben</CardTitle>
                            <CardDescription className="text-slate-600">Fügen Sie zwei Texte ein oder laden Sie Dateien hoch</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <label className="text-sm font-medium mb-1 block text-slate-700">Dokument 1</label>
                              <Textarea
                                placeholder="Text des ersten Dokuments..."
                                value={compareText1}
                                onChange={(e) => setCompareText1(e.target.value)}
                                rows={4}
                                className="border-slate-200 focus:border-primary"
                              />
                              <div className="flex items-center gap-2 mt-2">
                                <Input
                                  type="file"
                                  accept=".txt,.md,.csv,.json,.pdf"
                                  onChange={(e) => handleCompareFileChange(1, e)}
                                  className="flex-1 text-xs border-slate-200"
                                />
                                {compareFile1 && (
                                  <Badge variant="secondary" className="text-xs rounded-full bg-blue-100 text-primary">
                                    {compareFile1.name.substring(0, 15)}...
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium mb-1 block text-slate-700">Dokument 2</label>
                              <Textarea
                                placeholder="Text des zweiten Dokuments..."
                                value={compareText2}
                                onChange={(e) => setCompareText2(e.target.value)}
                                rows={4}
                                className="border-slate-200 focus:border-primary"
                              />
                              <div className="flex items-center gap-2 mt-2">
                                <Input
                                  type="file"
                                  accept=".txt,.md,.csv,.json,.pdf"
                                  onChange={(e) => handleCompareFileChange(2, e)}
                                  className="flex-1 text-xs border-slate-200"
                                />
                                {compareFile2 && (
                                  <Badge variant="secondary" className="text-xs rounded-full bg-blue-100 text-primary">
                                    {compareFile2.name.substring(0, 15)}...
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              onClick={handleCompare}
                              disabled={isComparing || !compareText1 || !compareText2}
                              className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all"
                            >
                              {isComparing ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Vergleiche...
                                </>
                              ) : (
                                <>
                                  <GitCompare className="mr-2 h-4 w-4" />
                                  Vergleichen
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>

                        <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
                          <CardHeader>
                            <CardTitle className="text-slate-900">Vergleichsergebnis</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {compareResult ? (
                              <div className="space-y-4 max-h-[400px] overflow-y-auto text-sm">
                                {compareResult.summary && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase mb-1">Zusammenfassung</h4>
                                    <p className="text-slate-700">{compareResult.summary}</p>
                                  </div>
                                )}
                                {compareResult.similarities?.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase mb-1">Gemeinsamkeiten</h4>
                                    <ul className="list-disc list-inside space-y-0.5 text-slate-700">
                                      {compareResult.similarities.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {compareResult.differences?.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-xs text-slate-500 uppercase mb-1">Unterschiede</h4>
                                    <ul className="list-disc list-inside space-y-0.5 text-slate-700">
                                      {compareResult.differences.map((d, i) => <li key={i}>{d}</li>)}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Export Buttons */}
                                <div className="flex gap-2 pt-4 border-t border-slate-200">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 rounded-xl"
                                    onClick={() => {
                                      exportComparisonToPDF({
                                        fileName1: compareFile1?.name || 'Dokument 1',
                                        fileName2: compareFile2?.name || 'Dokument 2',
                                        summary: compareResult.summary,
                                        similarities: compareResult.similarities,
                                        differences: compareResult.differences,
                                        uniqueDoc1: compareResult.uniqueDoc1,
                                        uniqueDoc2: compareResult.uniqueDoc2
                                      });
                                      toast.success("PDF heruntergeladen");
                                    }}
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    PDF
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 rounded-xl"
                                    onClick={() => {
                                      copyToClipboard(formatComparisonAsText({
                                        fileName1: compareFile1?.name || 'Dokument 1',
                                        fileName2: compareFile2?.name || 'Dokument 2',
                                        summary: compareResult.summary,
                                        similarities: compareResult.similarities,
                                        differences: compareResult.differences
                                      }));
                                      toast.success("In Zwischenablage kopiert");
                                    }}
                                  >
                                    <Copy className="h-4 w-4 mr-1" />
                                    Kopieren
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                                <GitCompare className="w-10 h-10 mb-3 opacity-30" />
                                <p className="text-sm">Vergleichsergebnis erscheint hier</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>

                    {/* Q&A Tab */}
                    <TabsContent value="ask">
                      <div className="grid lg:grid-cols-2 gap-6">
                        <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
                          <CardHeader>
                            <CardTitle className="text-slate-900">Frage stellen</CardTitle>
                            <CardDescription className="text-slate-600">Stellen Sie Fragen zu einem Dokument</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <label className="text-sm font-medium mb-1 block text-slate-700">Dokument</label>
                              <Textarea
                                placeholder="Fügen Sie das Dokument ein oder laden Sie eine Datei hoch..."
                                value={qaDocument}
                                onChange={(e) => setQaDocument(e.target.value)}
                                rows={5}
                                className="border-slate-200 focus:border-primary"
                              />
                              <div className="flex items-center gap-2 mt-2">
                                <Input
                                  type="file"
                                  accept=".txt,.md,.csv,.json,.pdf"
                                  onChange={handleQaFileChange}
                                  className="flex-1 text-xs border-slate-200"
                                />
                                {qaFile && (
                                  <Badge variant="secondary" className="text-xs rounded-full bg-blue-100 text-primary">
                                    {qaFile.name.substring(0, 15)}...
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium mb-1 block text-slate-700">Ihre Frage</label>
                              <Input
                                placeholder="Was möchten Sie wissen?"
                                value={qaQuestion}
                                onChange={(e) => setQaQuestion(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                                className="border-slate-200 focus:border-primary"
                              />
                            </div>
                            <Button
                              onClick={handleAsk}
                              disabled={isAsking || !qaDocument || !qaQuestion}
                              className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg transition-all"
                            >
                              {isAsking ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Suche Antwort...
                                </>
                              ) : (
                                <>
                                  <MessageSquareText className="mr-2 h-4 w-4" />
                                  Frage stellen
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>

                        <Card className="rounded-2xl border border-slate-200 shadow-lg bg-white">
                          <CardHeader>
                            <CardTitle className="text-slate-900">Antwort</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {qaAnswer ? (
                              <div className="space-y-4">
                                <div className="prose prose-sm max-w-none">
                                  <p className="whitespace-pre-wrap text-slate-700">{qaAnswer}</p>
                                </div>
                                
                                {/* Export Buttons */}
                                <div className="flex gap-2 pt-4 border-t border-slate-200">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 rounded-xl"
                                    onClick={() => {
                                      exportQAToPDF({
                                        question: qaQuestion,
                                        answer: qaAnswer,
                                        fileName: qaFile?.name
                                      });
                                      toast.success("PDF heruntergeladen");
                                    }}
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    PDF
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 rounded-xl"
                                    onClick={() => {
                                      copyToClipboard(`Frage: ${qaQuestion}\n\nAntwort: ${qaAnswer}`);
                                      toast.success("In Zwischenablage kopiert");
                                    }}
                                  >
                                    <Copy className="h-4 w-4 mr-1" />
                                    Kopieren
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                                <MessageSquareText className="w-10 h-10 mb-3 opacity-30" />
                                <p className="text-sm">Antwort erscheint hier</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Footer - matching main site */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Klartech Müritz. Alle Rechte vorbehalten.
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/impressum" className="text-slate-400 hover:text-white transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="text-slate-400 hover:text-white transition-colors">Datenschutz</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
